import { typeValidator } from '@chilipiper/utils'
import * as R from 'remeda'
import React, { ReactElement, createContext, useContext, useRef } from 'react'
import { AriaListBoxOptions, useListBox, Key } from 'react-aria'
import { ListState, Node, SelectState } from 'react-stately'
import styled from 'styled-components'
import invariant from 'tiny-invariant'
import { useThemeName } from '../../../../ThemeProviderClean'
import { Box, Flex, Text } from '../../../core-components'
import darkGenericGhost from '../../../images/darkGenericGhost.svg'
import genericGhost from '../../../images/genericGhost.svg'
import { ActionBar } from '../../action'
import { Spinner } from '../../feedback'
import { useElementSize } from '../../hooks'
import { SearchInput } from '../../input/search-input/SearchInput'
import { InContextModal } from '../../modal'
import { DynamicItem, DynamicSectionItem } from '../base-select/dynamicItemUtils'
import { ActiveItemProvider, SelectItemInternal } from '../select-item/SelectItem'
import { SelectSectionInternal } from '../select-section/SelectSection'
import { useSelectItemsData } from '../base-select/SelectItemContext'
import {
  VirtualItemWrapperFromContext,
  VirtualScrollProvider,
  useVirtualScrollableContainer,
} from './virtualScroll'

type BaseProps = AriaListBoxOptions<object> & {
  'data-id'?: string
  'data-test-id'?: string
  isLoading?: boolean
  keepMaxWidth?: boolean
  listBoxRef?: React.RefObject<HTMLUListElement | null>
  onClose?: () => void
  onFocusItem: (focusedKey: Key) => void
  onLeftKey?: () => void
  onRightKey?: () => void
  onSearchChange?: (value: string) => void
  search?: string
  state: SelectState<DynamicItem> | ListState<DynamicItem>
  useVirtualFocus?: boolean
  useVirtualScroll?: boolean
  variant?: 'regular' | 'nested'
  width?: number
} & (
    | {
        createModal?: never
        entityName?: never
        onCreate?: never
      }
    | {
        createModal?: never
        entityName: string
        onCreate: () => void
      }
    | {
        createModal: ReactElement
        entityName: string
        onCreate?: never
      }
  )
// eslint-disable-next-line complexity
export const SelectListBase = ({
  state,
  variant = 'regular',
  'data-id': dataId,
  'data-test-id': dataTestId = 'SelectList',
  isLoading,
  listBoxRef: propsListBoxRef,
  keyboardDelegate,
  width,
  onFocusItem,
  onClose,
  onLeftKey,
  onRightKey,
  useVirtualScroll,
  useVirtualFocus,
  keepMaxWidth = true,
  ...otherProps
}: BaseProps) => {
  const fallbackListBoxRef = React.useRef<HTMLUListElement>(null)
  const listBoxRef = propsListBoxRef ?? fallbackListBoxRef
  const hasVirtualFocus = useVirtualFocus || Boolean(otherProps.onSearchChange && keyboardDelegate)
  const itemsData = useSelectItemsData()
  const { listBoxProps } = useListBox(
    {
      shouldUseVirtualFocus: hasVirtualFocus,
      'aria-label': 'Select List',
      ...otherProps,
      isVirtualized: true,
    },
    state,
    listBoxRef
  )
  const isDarkTheme = useThemeName() === 'dark'

  const isAllHidden = [...state.collection].every(item => {
    if (item.type === 'section') {
      return [...(state.collection.getChildren?.(item.key) ?? [])].every(
        item => itemsData[item.key].isHidden
      )
    }
    return itemsData[item.key].isHidden
  })

  if (otherProps.createModal) {
    invariant(
      typeValidator(SelectListBaseCreateItemModal)(otherProps.createModal),
      'createModal must be SelectListBase.CreateModal'
    )
  }

  const handleClear = () => {
    if (otherProps.search === '') {
      onClose?.()
    }
  }
  const radius = variant === 'regular' ? 8 : 0

  const subscribeToScrollChange = useVirtualScrollableContainer(
    listBoxRef,
    isLoading ?? false,
    otherProps.search,
    useVirtualScroll
  )
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { width: wrapperWidth } = useElementSize(wrapperRef)

  return (
    <StyledWrapperFlexColumn
      variant={variant}
      maxHeight='inherit'
      borderRadius={radius}
      overflow='hidden'
      flexGrow={1}
      ref={wrapperRef}
      minWidth={keepMaxWidth ? wrapperWidth : 0}
      height='inherit'
    >
      <Flex
        maxHeight='inherit'
        flexDirection='column'
        flexGrow={1}
        bg='bg/dropdown'
        boxShadow={variant === 'regular' ? 'border/popover' : undefined}
        overflow='hidden'
        borderRadius={radius}
      >
        {otherProps.onSearchChange && (
          <SearchInput
            variant='nested'
            aria-label='Filter selection options'
            INTERNAL_onKeyDown={e => {
              switch (e.key) {
                case 'Enter': {
                  const focusedKey = state.selectionManager.focusedKey
                  if (focusedKey !== null) {
                    state.selectionManager.select(focusedKey)
                  }
                  break
                }
                case 'ArrowDown': {
                  const key =
                    state.selectionManager.focusedKey === null
                      ? keyboardDelegate?.getFirstKey?.()
                      : keyboardDelegate?.getKeyBelow?.(state.selectionManager.focusedKey)
                  if (key) {
                    onFocusItem(key)
                  }
                  e.preventDefault()
                  break
                }
                case 'ArrowUp': {
                  const focusedKey = state.selectionManager.focusedKey
                  const key =
                    focusedKey !== null ? keyboardDelegate?.getKeyAbove?.(focusedKey) : null
                  if (key) {
                    onFocusItem(key)
                  }
                  e.preventDefault()
                  break
                }
              }
            }}
            data-id={dataId}
            onChange={value => {
              const findKey = () => {
                for (const item of state.collection) {
                  if (item.type === 'section') {
                    const sectionItem = [...(state.collection.getChildren?.(item.key) ?? [])].find(
                      item => !itemsData[item.key].isHidden && !item.props.isDisabled
                    )
                    // eslint-disable-next-line max-depth
                    if (sectionItem) {
                      return sectionItem.key
                    }
                  } else if (!itemsData[item.key].isHidden && !item.props.isDisabled) {
                    return item.key
                  }
                }
              }
              const key = findKey()
              if (key) {
                onFocusItem(key)
              }
              otherProps.onSearchChange?.(value)
            }}
            placeholder='Search..'
            value={otherProps.search ?? ''}
            onClear={handleClear}
            data-test-id={`${dataTestId}-search`}
          />
        )}
        {otherProps.createModal && (
          <CreateItemContext.Provider value={{ assetName: otherProps.entityName }}>
            <ActiveItemProvider onSelect={R.doNothing()}>
              {otherProps.createModal}
            </ActiveItemProvider>
          </CreateItemContext.Provider>
        )}
        {otherProps.onCreate && (
          <Box boxShadow='border/row'>
            <ActionBar variant='nested'>
              <ActionBar.AddButton
                variant='minor'
                onClick={otherProps.onCreate ?? R.doNothing()}
                data-test-id={`${dataTestId}-create-new`}
              >
                Create New {otherProps.entityName}
              </ActionBar.AddButton>
            </ActionBar>
          </Box>
        )}
        <VirtualScrollProvider value={subscribeToScrollChange}>
          <OptionsWrapper
            display='flex'
            flexDirection='column'
            flexGrow={1}
            as='ul'
            width={useVirtualScroll ? width : undefined}
            overflowY='auto'
            overflowX='hidden'
            {...listBoxProps}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                return
              }
              if (e.key === 'ArrowLeft' && onLeftKey) {
                onLeftKey()
                return
              }
              if (e.key === 'ArrowRight' && onRightKey) {
                onRightKey()
                return
              }
              listBoxProps.onKeyDown?.(e)
            }}
            ref={listBoxRef}
          >
            {isAllHidden ? (
              <Flex
                flexGrow={1}
                p={6}
                flexDirection='column'
                justifyContent='center'
                alignItems='center'
                as='li'
              >
                {isLoading ? (
                  <Spinner data-test-id='SelectListBase-loading-spinner' />
                ) : (
                  <>
                    <Box
                      as='img'
                      alt=''
                      src={isDarkTheme ? darkGenericGhost : genericGhost}
                      w={12}
                      h={12}
                    />
                    <Text
                      display='block'
                      mt={2}
                      mb={7}
                      textStyle='body-monoline'
                      color='fg/moderate'
                    >
                      {otherProps.onCreate && !otherProps.search
                        ? `No ${otherProps.entityName} created`
                        : 'No results found'}
                    </Text>
                  </>
                )}
              </Flex>
            ) : (
              [...state.collection].map(item => {
                if (item.type === 'section') {
                  return (
                    <SelectSectionInternal
                      key={item.key}
                      section={item as Node<DynamicSectionItem>}
                      state={state}
                      useVirtualScroll={useVirtualScroll}
                    />
                  )
                }
                if (useVirtualScroll) {
                  return (
                    <VirtualItemWrapperFromContext
                      key={item.key}
                      itemKey={item.key}
                      selectionManager={state.selectionManager}
                    >
                      <SelectItemInternal
                        item={item}
                        state={state}
                        variant={
                          state.selectionManager.selectionMode === 'multiple' ? 'multi' : 'regular'
                        }
                      />
                    </VirtualItemWrapperFromContext>
                  )
                }
                return (
                  <SelectItemInternal
                    key={item.key}
                    item={item}
                    state={state}
                    variant={
                      state.selectionManager.selectionMode === 'multiple' ? 'multi' : 'regular'
                    }
                  />
                )
              })
            )}
          </OptionsWrapper>
        </VirtualScrollProvider>
      </Flex>
    </StyledWrapperFlexColumn>
  )
}

const CreateItemContext = createContext({ assetName: '' })
const SelectListBaseCreateItemModal = ({
  children,
  isOpen,
  onOpenChange,
}: {
  children: (props: { onCreated: () => void }) => ReactElement
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}) => {
  const { assetName } = useContext(CreateItemContext)

  return (
    <InContextModal
      aria-label={`Create new ${assetName}`}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={triggerProps => (
        <Box boxShadow='border/row'>
          <ActionBar variant='nested'>
            <ActionBar.AddButton
              variant='minor'
              onClick={R.doNothing()}
              data-test-id='AssetPicker-create-new'
              ariaProps={triggerProps}
            >
              Create New {assetName}
            </ActionBar.AddButton>
          </ActionBar>
        </Box>
      )}
    >
      {children({
        onCreated: () => onOpenChange(false),
      })}
    </InContextModal>
  )
}

SelectListBase.CreateModal = SelectListBaseCreateItemModal

const StyledWrapperFlexColumn = styled(Flex)<{ variant: BaseProps['variant'] }>`
  flex-direction: column;
  box-shadow: ${({ theme, variant }) =>
    variant === 'regular' ? theme.shadows['border/popover'] : undefined};
`
const OptionsWrapper = styled(Box)`
  list-style: none;
  outline: none;
`
